import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { language, date } from 'curvy';
import { TaskRoutes } from '@task-utils/routes';
import { Task } from '@task-utils/types';
import { User } from '@task-modules/users';
import { format_time } from '@task-utils/utils';
import { shelf_filler_translations } from './shelf-filler.trans';
import { WORK_TYPE_TRANS, SHELF_STATUS_UI } from './shelf-filler.shared';

type PlanWorker = { user_id: number; first_name: string | null; last_name: string | null };
type ShelfSupplier = { supplier_company_id: number; name: string };
type BatchRow = { date: Date | null; time: Date | null };

export type PlanFormResult = {
	mode: 'single' | 'batch';
	is_edit: boolean;
	shelf_visit_id?: number;
	supplier_company_id: number | null;
	location_id: number;
	planned_work_type_ids: number[];
	planned_note: string | null;
	planned_worker_user_ids: number[];
	planned_visit_date?: string;
	planned_visit_time?: string | null;
	rows?: { planned_visit_date: string; planned_visit_time: string | null }[];
};

@Component({
	selector: 'task-create-edit-shelf-plan',
	templateUrl: './create-edit-shelf-plan.component.html',
	styleUrls: ['./create-edit-shelf-plan.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class CreateEditShelfPlanComponent implements OnInit, OnChanges {
	@Input() visit: Task.ShelfVisit | null = null;
	@Input() can_edit = false;
	@Input() can_no_show = false;

	@Output() result = new EventEmitter<PlanFormResult | null>();
	@Output() action = new EventEmitter<'delete' | 'no_show'>();

	is_edit = false;
	is_supplier_admin = false;

	mode: 'single' | 'batch' = 'single';

	supplier: ShelfSupplier | null = null;
	location: Task.Location | null = null;
	note = '';

	work_type_options = [
		Task.SHELF_WORK_TYPE.SHELF_STOCKING,
		Task.SHELF_WORK_TYPE.EXTRA_DISPLAY_STOCKING,
		Task.SHELF_WORK_TYPE.ASSORTMENT_CONTROL,
		Task.SHELF_WORK_TYPE.SALES_VISIT,
		Task.SHELF_WORK_TYPE.OTHER,
	].map(id => ({ id, trans: WORK_TYPE_TRANS[id], selected: false }));

	single_date: Date | null = null;
	single_time: Date | null = null;

	planned_workers: PlanWorker[] = [];

	rows: BatchRow[] = [];
	week_start: Date | null = null;

	constructor() { language.load_translations(shelf_filler_translations); }

	async ngOnInit() {
		this.is_supplier_admin = await User.currentTypeIs(Task.USER_TYPE.SUPPLIER_ADMIN);
		if (this.visit && !this.is_edit) { this.populate_from_visit(); }
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['visit'] && this.visit && !this.is_edit) { this.populate_from_visit(); }
	}

	populate_from_visit() {
		const v = this.visit;
		if (!v) { 
			return; 
		}
		this.is_edit = true;
		this.mode = 'single';

		this.supplier = { supplier_company_id: v.supplier_company_id, name: v.supplier_name ?? '' };
		
		this.location = {
			location_id: v.location_id,
			name: v.location_name ?? '',
			cin: v.location_cin ?? '',
		} as Task.Location;

		this.note = v.planned_note ?? '';
		this.single_date = v.planned_visit_date ? new Date(v.planned_visit_date) : null;
		this.single_time = v.planned_visit_time ? this.time_to_date(v.planned_visit_time) : null;

		const ids = new Set<number>(v.planned_work_type_ids ?? []);
		this.work_type_options.forEach(o => o.selected = ids.has(o.id));
		this.planned_workers = (v.planned_workers ?? []).map(w => this.to_plan_worker(w));
	}

	worker_company_id(): number | null {
		return this.supplier?.supplier_company_id ?? (this.is_supplier_admin ? (User.currentUser?.company_id ?? null) : null);
	}
	workers_enabled(): boolean {
		return !this.plan_locked() && this.worker_company_id() != null;
	}
	search_workers = async (q: string): Promise<PlanWorker[]> => {
		const company_id = this.worker_company_id();
		if (company_id == null) { 
			return []; 
		}  
		const res = await TaskRoutes.users.api_user_search(
			{ search: [q, `${company_id}`], attributes: ['first_name+ +last_name', '#=:company_id'] },
			{ page_no: 1, page_size: 25 });
		return ((res.data ?? []) as Task.User[]).map(u => this.to_plan_worker(u));
	};

	to_plan_worker(u: PlanWorker): PlanWorker {
		return { user_id: u.user_id, first_name: u.first_name, last_name: u.last_name };
	}

	format_worker_name = (w: PlanWorker) => w ? `${w.first_name ?? ''} ${w.last_name ?? ''}`.trim() : '';

	on_supplier_change(s: ShelfSupplier | null) {
		this.supplier = s;
		this.planned_workers = [];
	}

	plan_locked() {
		return !!this.visit && this.visit.status_id !== Task.STATUS.CREATED;
	}

	lock_message(): string {
		switch (this.visit?.status_id) {
			case Task.STATUS.UNDONE:       return 'PUNJENJE.FORM.LOCKED_NO_SHOW';
			case Task.STATUS.EXECUTED:     return 'PUNJENJE.FORM.LOCKED_DONE';
			case Task.STATUS.IN_EXECUTION: return 'PUNJENJE.FORM.LOCKED_IN_PROGRESS';
			default:                       return 'PUNJENJE.FORM.LOCKED';
		}
	}

	is_visit_only() {
		return !!this.visit && !this.visit.planned; 
	}

	is_editable() {
		if (this.is_visit_only()) { return false; }
		if (this.visit && !this.can_edit) { return false; }
		return true;
	}

	show_delete() { 
		return this.can_edit && !!this.visit && !this.plan_locked(); 
	}

	show_no_show() { 
		return this.can_no_show && !!this.visit && this.visit.planned && this.visit.status_id === Task.STATUS.CREATED; 
	}

	modal_title() {
		if (this.is_visit_only()) { return 'PUNJENJE.DETAIL.ADHOC_VISIT'; }
		return this.is_edit ? 'PUNJENJE.FORM.EDIT' : 'PUNJENJE.FORM.NEW';
	}

	derived_status(): Task.STATUS {
		return this.visit?.status_id ?? Task.STATUS.CREATED;
	}

	status_struct(status: Task.STATUS) {
		const ui = SHELF_STATUS_UI[status];
		return {
			icon: ui?.icon ?? 'radio_button_unchecked',
			color: ui?.color ?? Task.STATUS_COLORS.GRAY,
			status_name: language.translate(ui?.trans ?? ''),
		};
	}

	entity_title() {
		if (this.visit && this.visit.planned) { return this.visit.supplier_name || ('#' + this.visit.supplier_company_id); }
		const base = language.translate('PUNJENJE.DETAIL.ADHOC_VISIT');
		if (this.visit?.shelf_visit_id) {
			return `${base} #${this.visit.shelf_visit_id}`;
		}
		return base;
	}

	store_label() {
		if (this.visit) {
			const cin = this.visit.location_cin ? this.visit.location_cin + ' · ' : '';
			return `${cin}${this.visit.location_name ?? ''}`.trim() || ('#' + this.visit.location_id);
		}
		return '';
	}

	type_names_str() {
		const ids = this.visit?.planned_work_type_ids ?? [];
		return ids.length ? ids.map(id => language.translate(WORK_TYPE_TRANS[id])).join(', ') : null;
	}

	tiles() {
		return this.is_visit_only() ? [{ title: 'PUNJENJE.ADH', color: 'var(--secondary)' }] : [];
	}

	has_execution() {
		return !!this.visit && (!!this.visit.check_in_time || (this.visit.work_items?.length ?? 0) > 0);
	}

	work_items() { 
		return this.visit?.work_items ?? []; 
	}

	work_type_name(id: Task.SHELF_WORK_TYPE) { 
		return language.translate(WORK_TYPE_TRANS[id]); 
	}

	visit_hours() { 
		return (this.visit?.work_items ?? []).reduce((s, w) => s + (w.hours || 0), 0); 
	}

	fmt_time_str(t: Task.TIME | null): string | null { 
		return t ? format_time(this.time_to_date(t)) : null; 
	}

	work_type_ids() { 
		return this.work_type_options.filter(o => o.selected).map(o => o.id); 
	}

	type_valid() { 
		return this.work_type_ids().length > 0; 
	}

	search_suppliers = async (q: string): Promise<ShelfSupplier[]> => {
		const res = await TaskRoutes.shelf.api_shelf_suppliers_get();
		const needle = (q ?? '').toLowerCase();
		return (res.data ?? []).filter((s: ShelfSupplier) => (s.name ?? '').toLowerCase().includes(needle));
	};

	format_supplier_name = (s: ShelfSupplier) => s ? s.name : '';

	search_locations = async (q: string): Promise<Task.Location[]> => {
		const res = await TaskRoutes.locations.api_location_search_visible(
			{ search: [q, Task.LOCATION_TYPE.STORE, true], attributes: ['cin+ +name', '#=:location_type_id', 'b=:nfc'] },
			{ page_no: 1, page_size: 50 });
		return res.data;
	};

	format_location_name = (loc: Task.Location) => loc ? `${loc.cin ?? ''} ${loc.name}`.trim() : '';

	set_mode(m: 'single' | 'batch') { this.mode = m; }

	add_row() { 
		this.rows.push({ date: null, time: null }); 
	}

	remove_row(i: number) { 
		this.rows.splice(i, 1); 
	}

	add_week() { this.add_weeks(1); }
	add_month() { this.add_weeks(4); }

	add_weeks(n: number) {
		if (!this.week_start) { return; }
		for (let i = 0; i < 7 * n; i++) {
			this.rows.push({ date: date.add(this.week_start, date.duration({ date: i })), time: null });
		}
		this.week_start = null;
	}

	time_to_date(t: string | Date): Date {
		if (t instanceof Date) { return t; }
		const time_only = new Date('1970-01-01T' + t);
		return isNaN(+time_only) ? new Date(t) : time_only;
	}

	can_save(): boolean {
		if (!this.location || !this.type_valid()) { return false; }
		if (!this.is_supplier_admin && !this.is_edit && !this.supplier) { return false; }
		if (this.is_edit || this.mode === 'single') { return !!this.single_date; }
		return this.rows.length > 0 && this.rows.every(r => !!r.date);
	}

	done() {
		if (!this.can_save() || !this.location) { return; }
		const base = {
			supplier_company_id: this.supplier?.supplier_company_id ?? null,
			location_id: this.location.location_id,
			planned_work_type_ids: this.work_type_ids(),
			planned_note: this.note?.trim() || null,
			planned_worker_user_ids: [...new Set(this.planned_workers.map(w => w.user_id))],
		};

		if (this.is_edit && this.visit) {
			this.result.emit({
				mode: 'single', is_edit: true, shelf_visit_id: this.visit.shelf_visit_id, ...base,
				planned_visit_date: this.single_date!.toISOString().split('T')[0],
				planned_visit_time: this.single_time ? format_time(this.single_time) : null,
			});
		} else if (this.mode === 'single') {
			this.result.emit({
				mode: 'single', is_edit: false, ...base,
				planned_visit_date: this.single_date!.toISOString().split('T')[0],
				planned_visit_time: this.single_time ? format_time(this.single_time) : null,
			});
		} else {
			this.result.emit({
				mode: 'batch', is_edit: false, ...base,
				rows: this.rows.map(r => ({
					planned_visit_date: r.date!.toISOString().split('T')[0],
					planned_visit_time: r.time ? format_time(r.time) : null,
				})),
			});
		}
	}

	cancel() { 
		this.result.emit(null); 
	}

	do_delete() { 
		this.action.emit('delete'); 
	}

	do_no_show() { 
		this.action.emit('no_show'); 
	}
}
